import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { Difficulty, InfiltratorSetting, MapId, Role } from '@il-colpo/engine';
import { ROLES_BY_PLAYER_COUNT } from '@il-colpo/engine';
import { useLooseT } from '../../game/text/loose.ts';
import { useGameStore, type BotLevelChoice } from '../../game/gameStore.ts';
import { useSettings } from '../../state/settingsStore.ts';
import { Button, Panel, ScreenFrame, SegmentedControl } from '../../ui/index.ts';
import styles from './SoloSetup.module.css';

const MAPS: readonly MapId[] = ['bank', 'museum', 'villa', 'express'];
const DIFFICULTIES: readonly Difficulty[] = ['easy', 'normal', 'pro', 'legend'];
const INFILTRATOR: readonly InfiltratorSetting[] = ['never', 'always', 'normal'];
const BOT_LEVELS: readonly BotLevelChoice[] = ['auto', 'easy', 'medium', 'hard', 'expert'];

/** Solo (GDD §11): you and 2–4 bots; you are always a thief; the Mole, if any, is a bot. */
export function SoloSetup() {
  const { t } = useTranslation('setup');
  const g = useLooseT('game');
  const navigate = useNavigate();
  const { language } = useSettings();
  const start = useGameStore((s) => s.start);
  const starting = useGameStore((s) => s.starting);

  const [players, setPlayers] = useState<3 | 4 | 5>(4);
  const [mapId, setMapId] = useState<MapId>('bank');
  const [difficulty, setDifficulty] = useState<Difficulty>('normal');
  const [infiltrator, setInfiltrator] = useState<InfiltratorSetting>('normal');
  const [role, setRole] = useState<Role | 'random'>('random');
  const [botLevel, setBotLevel] = useState<BotLevelChoice>('auto');

  const roles = ROLES_BY_PLAYER_COUNT[players] ?? [];
  const chosenRole = role !== 'random' && roles.includes(role) ? role : 'random';

  const go = async () => {
    await start({
      mode: 'solo',
      players,
      mapId,
      difficulty,
      infiltrator,
      role: chosenRole,
      botLevel,
      humanName: t('you'),
      language,
    });
    void navigate('/game');
  };

  return (
    <ScreenFrame title={t('soloTitle')} backTo="/play" tone="blueprint">
      <div className={styles['form']}>
        <p className={styles['intro']}>{t('soloIntro')}</p>
        <Panel tone="blueprint" className={styles['panel']}>
          <SegmentedControl
            label={t('players')}
            value={players}
            onChange={setPlayers}
            options={[3, 4, 5].map((n) => ({
              value: n as 3 | 4 | 5,
              label: t('playersOption', { count: n }),
            }))}
          />
          <SegmentedControl
            label={t('map')}
            value={mapId}
            onChange={setMapId}
            options={MAPS.map((m) => ({ value: m, label: g(`maps.${m}.name`) }))}
          />
          <SegmentedControl
            label={t('difficulty')}
            value={difficulty}
            onChange={setDifficulty}
            options={DIFFICULTIES.map((d) => ({ value: d, label: g(`difficulty.${d}`) }))}
          />
          <SegmentedControl
            label={t('infiltrator')}
            value={infiltrator}
            onChange={setInfiltrator}
            options={INFILTRATOR.map((i) => ({ value: i, label: t(`infiltratorOptions.${i}`) }))}
          />
          <p className={styles['hint']}>{t(`infiltratorHint.${infiltrator}`)}</p>
          <SegmentedControl
            label={t('role')}
            value={chosenRole}
            onChange={setRole}
            options={[
              { value: 'random' as const, label: t('roleRandom') },
              ...roles.map((r) => ({ value: r, label: g(`roles.${r}`) })),
            ]}
          />
          <SegmentedControl
            label={t('botLevel')}
            value={botLevel}
            onChange={setBotLevel}
            options={BOT_LEVELS.map((l) => ({ value: l, label: t(`botLevels.${l}`) }))}
          />
        </Panel>
        <Button size="lg" icon="play" loading={starting} onClick={() => void go()} block>
          {t('start')}
        </Button>
      </div>
    </ScreenFrame>
  );
}
